import { DateTime } from '@ksyos/date-time';
import { Alert, Form, FormGroup } from '@ksyos/design-system';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { FilesUrlBase, PractitionerRoleInRequest, ProcessEntity, ServiceCode, ToastType } from '@common/enums';
import { ServicePermissionsAuthorizations } from '@common/interfaces';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { getSelectedLanguage } from '@frontend/translate';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, FormDivider, HeaderDivider } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { DateField } from '../../../../fields/Date';
import { NumberField } from '../../../../fields/Number';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { BloeddrukAddEdit } from '../../TCOR/v1/BloeddrukAddEdit';
import { AssetDropzoneWithActions } from '../../utils';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { TfoV1FollowingSteps } from './TfoV1FollowingSteps';

export function TfoV1RequestAddEdit(props: Readonly<ICustomControlRequestAddEditProps>) {
    const translate = getSelectedLanguage();
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string>();
    const {
        servicePermissions: { [ServiceCode.TFO]: tfoPermissions },
    } = useSelector((state: IReduxState) => state.authentication);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form submitOnEnter={false}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={props.request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={props.service.id}
                />

                {fileUploadErrorMessage && <Alert appearance="warning">{fileUploadErrorMessage}</Alert>}
                {tfoPermissions?.[ServicePermissionsAuthorizations.canAddAssets] && (
                    <FormGroup
                        hasErrors={Boolean(fileUploadErrorMessage)}
                        isRequired={true}
                        label={translate.request.referralLetter}
                    >
                        <AssetDropzoneWithActions
                            acceptedFiles=".pdf"
                            assets={props.request?.customControlData.files ?? []}
                            assetsBaseUrl={FilesUrlBase.requests}
                            maxFiles={1}
                            onChange={async (assets) => {
                                await props.handleChangeCustomControl({ files: assets });
                            }}
                            onUploading={async (isUploading2) => setIsUploading(isUploading2)}
                            processActivityId={props.request.id}
                            service={props.service}
                        />
                    </FormGroup>
                )}

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <HeaderDivider text={translate.request.request} />

                {addEditForm()}

                <TfoV1FollowingSteps />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            key="cancel"
                            revertChanges={props.revertRequestChanges}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={isButtonDisabled || isUploading}
                            key="save"
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={isButtonDisabled || isUploading}
                            key="saveSend"
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setIsButtonDisabled(true);

        if (
            tfoPermissions?.canAddAssets &&
            saveType === 'final' &&
            (!props.request.customControlData.files || props.request.customControlData.files.length === 0)
        ) {
            setFileUploadErrorMessage(translate.dropzone.addReferralLetter);
            setIsButtonDisabled(false);

            return;
        }

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setIsButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translate.toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translate.toast.draftDefaultMessage
            );
        }
        await props.onSave(requestId);
    }

    function addEditForm() {
        return (
            <FormContextProvider
                value={{
                    errors: props.errors,
                    item: props.request.customControlData,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    placeholderGroupName: 'request',
                }}
            >
                <NumberField
                    automation="hba1c"
                    error={props.errors.get('customControlData.hba1c')}
                    fieldKey="hba1c"
                    hasErrors={props.errors.has('customControlData.hba1c')}
                    label="HbA1c"
                    placeholder=""
                    text="mmol/mol"
                    width="small"
                />

                <BloeddrukAddEdit
                    automation="bloodPressure"
                    customControlData={props.request.customControlData}
                    errors={props.errors}
                    handleChange={props.handleChangeCustomControl}
                    validateOne={props.validateOneCustomControl}
                />

                <DateField
                    error={props.errors.get('customControlData.diabetes_sinds')}
                    fieldKey="diabetes_sinds"
                    hasErrors={props.errors.has('customControlData.diabetes_sinds')}
                    label="Diabetes sinds"
                    maxDate={DateTime.now().format()}
                    placeholder={`${translate.common.datePlaceholder}`}
                    size="medium"
                    value={props.request.customControlData.diabetes_sinds}
                />
                <TextAreaField
                    automation="history"
                    error={props.errors.get('customControlData.oculaire_voorgeschiedenis')}
                    fieldKey="oculaire_voorgeschiedenis"
                    hasErrors={props.errors.has('customControlData.oculaire_voorgeschiedenis')}
                    label="Oculaire voorgeschiedenis"
                    placeholder="Omschrijf de oculaire voorgeschiedenis"
                />
                <RadioGroupField
                    automation="complications"
                    error={props.errors.get('customControlData.andere_dm_complicaties')}
                    fieldKey="andere_dm_complicaties"
                    hasErrors={props.errors.has('customControlData.andere_dm_complicaties')}
                    label="Andere DM complicaties"
                    onDoValidate={async () => {
                        await props.validateOneCustomControl('andere_dm_complicaties');
                        await props.validateOneCustomControl('toelichting');
                    }}
                    options={['Nee', 'Ja']}
                />
                <TextAreaField
                    automation="remarks"
                    error={props.errors.get('customControlData.toelichting')}
                    fieldKey="toelichting"
                    hasErrors={props.errors.has('customControlData.toelichting')}
                    isRequired={props.request.customControlData.andere_dm_complicaties === 'Ja'}
                    label="Toelichting"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                />
            </FormContextProvider>
        );
    }
}

export function tfoV1InitializeCustomControlData() {
    return {
        andere_dm_complicaties: null,
        bloeddruk_diastolisch: null,
        bloeddruk_systolisch: null,
        diabetes_sinds: null,
        hba1c: null,
        oculaire_voorgeschiedenis: null,
        toelichting: null,
    };
}
